// JSON-LD surface registry — single source of truth mapping every
// `ShareSurface` kind to its Schema.org structured-data builder.
//
// ─────────────────────────────────────────────────────────────────
// HOW TO ADD JSON-LD FOR A SURFACE
// ─────────────────────────────────────────────────────────────────
//   1. Implement the builder in `lib/og/json-ld/<surface>.ts` exporting
//      `buildXxxJsonLd(surface, ctx): JsonLdPayload`. Use existing
//      builders as the template — pick the closest Schema.org type,
//      reference site-wide nodes via `@id` instead of duplicating.
//   2. Wire it into the registry below.
//   3. Add `<JsonLdScript data={…} />` to the page that owns the URL,
//      INSIDE the page render (not in metadata). Build the data via
//      `buildSurfaceJsonLd(surface, ctx)`.
//
// The mapped type below produces a TS error if a new `kind` is added to
// `ShareSurface` and not registered here — same compile-time
// exhaustiveness guarantee as `OG_SURFACE_REGISTRY`.

import type { ShareSurface } from '@/lib/share/events';

import { buildGiftJsonLd } from './gift';

export type { BuildArticleOptions } from './article';
export { buildArticleJsonLd } from './article';
export type { BreadcrumbItem } from './breadcrumb';
export { buildBreadcrumbJsonLd } from './breadcrumb';
export type { FaqEntry } from './faq';
export { buildFaqJsonLd } from './faq';
export type { BuildItemListOptions, ItemListEntry } from './item-list';
export { buildItemListJsonLd } from './item-list';
export { buildWebApplicationJsonLd } from './web-application';

import { buildCompetitionJsonLd } from './competition';
import { buildGiftThankJsonLd } from './gift-thank';
import { buildMarketJsonLd } from './market';
import { buildPnlMarketJsonLd, buildPnlPositionJsonLd, buildPrizeClaimJsonLd } from './pnl';
import { buildProfileJsonLd } from './profile';
import { buildProposalJsonLd } from './proposal';
import { buildProposalBackJsonLd } from './proposal-back';
import { buildRankJsonLd } from './rank';
import { buildRankBoostersJsonLd } from './rank-boosters';
import { buildRankCreatorsJsonLd } from './rank-creators';
import { buildReferralJsonLd } from './referral';
import { buildStaticPageJsonLd } from './static-page';
import { buildSupportMainnetJsonLd } from './support-mainnet';
import { buildThesisJsonLd } from './thesis';
import { buildTradeJsonLd } from './trade';
import type { JsonLdBuilder } from './types';

export { JsonLdScript } from './script';
export { buildOrganizationJsonLd, buildSiteJsonLd, buildWebSiteJsonLd } from './site';
export type { JsonLdBuilder, JsonLdBuilderContext, JsonLdNode, JsonLdPayload } from './types';

/** Mapped type ensures every `ShareSurface['kind']` has a registered
 *  JSON-LD builder at compile time. */
export const JSON_LD_SURFACE_REGISTRY: {
  [K in ShareSurface['kind']]: JsonLdBuilder<Extract<ShareSurface, { kind: K }>>;
} = {
  market: buildMarketJsonLd,
  live_event: (
    _surface: Extract<ShareSurface, { kind: 'live_event' }>,
    ctx: import('./types').JsonLdBuilderContext,
  ): import('./types').JsonLdPayload =>
    buildStaticPageJsonLd({ kind: 'static_page', slug: 'markets' }, ctx),
  competition: buildCompetitionJsonLd,
  competition_rank: (
    surface: Extract<ShareSurface, { kind: 'competition_rank' }>,
    ctx: import('./types').JsonLdBuilderContext,
  ): import('./types').JsonLdPayload =>
    buildProfileJsonLd({ kind: 'profile', userId: surface.userId }, ctx),
  proposal: buildProposalJsonLd,
  gift: buildGiftJsonLd,
  gift_thank: buildGiftThankJsonLd,
  support_mainnet: buildSupportMainnetJsonLd,
  // `early_access` is a thin, gated invite landing — no rich structured
  // data is appropriate (the page body humans see is the access gate), so
  // it contributes no JSON-LD. Mirrors `globe` above.
  early_access: (): import('./types').JsonLdPayload => null,
  referral_link: buildReferralJsonLd,
  referral_banner: buildReferralJsonLd,
  thesis: buildThesisJsonLd,
  trade: buildTradeJsonLd,
  boost: (
    surface: Extract<ShareSurface, { kind: 'boost' }>,
    ctx: import('./types').JsonLdBuilderContext,
  ): import('./types').JsonLdPayload =>
    buildMarketJsonLd({ kind: 'market', marketPubkey: surface.marketPubkey }, ctx),
  proposal_back: buildProposalBackJsonLd,
  rank: buildRankJsonLd,
  rank_boosters: buildRankBoostersJsonLd,
  rank_creators: buildRankCreatorsJsonLd,
  // `rank_backers` describes the same person/profile as the portfolio page
  // (the board ranking lives in the OG image, not the URL), so it reuses the
  // profile builder keyed on userId. Mirrors `rank` / `streak` above.
  rank_backers: (
    surface: Extract<ShareSurface, { kind: 'rank_backers' }>,
    ctx: import('./types').JsonLdBuilderContext,
  ): import('./types').JsonLdPayload =>
    buildProfileJsonLd({ kind: 'profile', userId: surface.userId }, ctx),
  profile: buildProfileJsonLd,
  backer_rewards: (
    surface: Extract<ShareSurface, { kind: 'backer_rewards' }>,
    ctx: import('./types').JsonLdBuilderContext,
  ): import('./types').JsonLdPayload =>
    buildProfileJsonLd({ kind: 'profile', userId: surface.userId }, ctx),
  // `streak` shares the canonical URL of the user's portfolio page (same
  // as `profile`) — the streak figures live in the OG image only, not the
  // URL — so the JSON-LD reuses the profile builder. Mirrors
  // `market_prize_pool` above.
  streak: (
    surface: Extract<ShareSurface, { kind: 'streak' }>,
    ctx: import('./types').JsonLdBuilderContext,
  ): import('./types').JsonLdPayload =>
    buildProfileJsonLd({ kind: 'profile', userId: surface.userId }, ctx),
  pnl_position: buildPnlPositionJsonLd,
  pnl_market: buildPnlMarketJsonLd,
  prize_claim: buildPrizeClaimJsonLd,
  // `market_prize_pool` shares the same canonical URL as the market
  // detail page, so it reuses the market builder. Surface-specific
  // payload fields (like `prizePoolD6`) aren't structured-data signals
  // — the JSON-LD describes the page, which is identical.
  market_prize_pool: (
    surface: Extract<ShareSurface, { kind: 'market_prize_pool' }>,
    ctx: import('./types').JsonLdBuilderContext,
  ): import('./types').JsonLdPayload =>
    buildMarketJsonLd({ kind: 'market', marketPubkey: surface.marketPubkey }, ctx),
  // `market_result` shares the same canonical URL as the market detail
  // page (per-wallet info is communicated via the OG image only, not in
  // the URL), so the JSON-LD reuses the market builder. Mirrors
  // `market_prize_pool` above.
  market_result: (
    surface: Extract<ShareSurface, { kind: 'market_result' }>,
    ctx: import('./types').JsonLdBuilderContext,
  ): import('./types').JsonLdPayload =>
    buildMarketJsonLd({ kind: 'market', marketPubkey: surface.marketPubkey }, ctx),
  // `market_result_public` shares the same canonical URL as the market detail
  // page (the resolution result is communicated via the OG image only, not in
  // the URL), so the JSON-LD reuses the market builder. Mirrors `market_result`.
  market_result_public: (
    surface: Extract<ShareSurface, { kind: 'market_result_public' }>,
    ctx: import('./types').JsonLdBuilderContext,
  ): import('./types').JsonLdPayload =>
    buildMarketJsonLd({ kind: 'market', marketPubkey: surface.marketPubkey }, ctx),
  // `winner` (the `/winners/{mint}` page) describes the same underlying
  // resolved market/event as the market detail page, so the JSON-LD reuses
  // the market builder keyed on the parent market pubkey. The token-specific
  // framing lives in the title/description + OG image, not the structured
  // data. Mirrors `market_result` / `market_prize_pool` above.
  winner: (
    surface: Extract<ShareSurface, { kind: 'winner' }>,
    ctx: import('./types').JsonLdBuilderContext,
  ): import('./types').JsonLdPayload =>
    buildMarketJsonLd({ kind: 'market', marketPubkey: surface.marketPubkey }, ctx),
  // `globe` is an overlay on the feed pages (shared as `/<feed>?globe=<mode>`),
  // not a page of its own — the underlying feed page owns the structured data,
  // so the globe surface contributes no JSON-LD.
  globe: (): import('./types').JsonLdPayload => null,
  static_page: buildStaticPageJsonLd,
  // `waitlist_position` piggy-backs on the gated `/early-access` landing — no
  // rich structured data (same as `early_access`); contributes no JSON-LD.
  waitlist_position: (): import('./types').JsonLdPayload => null,
  // `rewards_rank` is the gated rewards-waitlist standing card — the public
  // body humans see is a thin profile; no rich structured data is
  // appropriate (mirrors `early_access` / `waitlist_position`).
  rewards_rank: (): import('./types').JsonLdPayload => null,
};

/** Resolve and invoke the builder for a surface. Returns `null` when no
 *  structured data is appropriate (e.g. ephemeral CTAs). */
export function buildSurfaceJsonLd<K extends ShareSurface['kind']>(
  surface: Extract<ShareSurface, { kind: K }>,
  ctx: import('./types').JsonLdBuilderContext,
): import('./types').JsonLdPayload {
  // Mapped-type per-key descriptor narrows the builder's surface arg to
  // the matching kind; same pattern used in lib/og/url.ts and
  // lib/og/metadata.ts. Compile-time exhaustiveness on the registry
  // gives us safety; runtime cast widens to the union to invoke.
  // ALLOW-UNKNOWN: see comment above
  const builder = JSON_LD_SURFACE_REGISTRY[surface.kind] as unknown as JsonLdBuilder;
  return builder(surface, ctx);
}
